import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { makeAutoObservable } from 'mobx'
import qs from 'qs'

import type { COLOR } from '@/constant'
import type { WorksheetDesignTemplateT } from '@/contentComponents/common/WorksheetDesign/DesignTemplate.service'
import type { RouteName } from '@/router'

import type { WorkbookDesignTemplate } from '../DesignTemplate/WorkbookCoverDesignTemplate'

export const MAKE_LESSON_MATERIAL_STEP1_TYPE: Record<string, common.MakeWorksheetBy> = {
  단원유형별: 'CONCEPT',
  시중교재: 'PUBLIC_WORKBOOK',
  수능모의고사: 'PRACTICE_TEST',
  시그니처교재: 'SIGNATURE_WORKBOOK',
  학교별기출: 'SCHOOL_TEST',
  기출업로드: 'CUSTOM_PAPER',
}

type MakeLessonMaterialContext<
  FixOption extends common.FixOption,
  EntryPathType extends FixOption extends 'NONE'
    ? keyof typeof MAKE_LESSON_MATERIAL_STEP1_TYPE
    : never = FixOption extends 'NONE' ? keyof typeof MAKE_LESSON_MATERIAL_STEP1_TYPE : never,
> = FixOption extends 'NONE'
  ? EntryPathType extends '시중교재' | '시그니처교재'
    ? {
        stepOneType: (typeof MAKE_LESSON_MATERIAL_STEP1_TYPE)[EntryPathType]
        fixOption: 'NONE'
        workbookType: WorkbookEntity.Workbook['type']
        entryPath: ValueOf<typeof RouteName>
        destinationPath?: ValueOf<typeof RouteName>
      }
    : {
        stepOneType: (typeof MAKE_LESSON_MATERIAL_STEP1_TYPE)[EntryPathType]
        fixOption: 'NONE'
        entryPath: ValueOf<typeof RouteName>
        destinationPath?: ValueOf<typeof RouteName>
      }
  : {
      fixOption: FixOption
      entryPath: ValueOf<typeof RouteName>
      destinationPath?: ValueOf<typeof RouteName>
      originPublicWorkbookId?: WorkbookEntity.Workbook['id']
      weakWorkbookSourceId?: WorkbookEntity.Workbook['id']
      weakWorkbookPartitionType?: 'PAGE' | 'PROBLEM'
    }

// 수정, 1단계 - 만들기 공통으로 쓰이는 params만 정의
// 수정시 필요한 학습지 상세정보들은 정의 안함. - Ex) title, writer
// TODO: MakeLessonMaterialContext로 언젠가 다 바꾸기..
export class MakeLessonMaterialCommonParams<
  FixOption extends common.FixOption,
  EntryPathType extends FixOption extends 'NONE'
    ? keyof typeof MAKE_LESSON_MATERIAL_STEP1_TYPE
    : never = FixOption extends 'NONE' ? keyof typeof MAKE_LESSON_MATERIAL_STEP1_TYPE : never,
> {
  sameOptionUse: 'USE' | 'NONE' = 'NONE' // 기존 옵션을 다시 쓸지 여부
  filterId = '' // 필터 고유아이디
  problemSortType?: common.MakeWorksheetProblemSortType
  download?: boolean
  version = 3
  designTemplateId?: WorkbookDesignTemplate['id'] // 수정/복제후 수정 시 필요
  priorityLayoutId?: WorksheetDesignTemplateT['id'] // 만드는 학습지의 종류에 따라 우선 적용할 레이아웃 아이디(로컬스토리지 보다 우선함)
  priorityLayoutColor?: (typeof COLOR)[number]['id'] // 만드는 학습지의 종류에 따라 우선 적용할 색상
  originalFilterId?: string // 기출업로드에서 원본+유사 동시 활성화 시 원본 필터 ID
  hasOriginal?: boolean // 기출업로드 원본 학습지 활성화 여부
  saveOption?: boolean //  나의 DB 저장 여부
  hasSimilar?: boolean // 기출업로드 유사 학습지 활성화 여부
  similarX?: 0 | 1 | 2 | 3 // 기출업로드 유사 문제 배수
  similarLevel?: 'AS_IS' | 'HARDER' | 'EASIER' // 기출업로드 유사 문제 난이도
  filterType?: common.WorksheetType // 어떤 종류로 만들어 졌는지 // 단원유형, 기출업로드 등등
  entryContext: MakeLessonMaterialContext<FixOption, EntryPathType> = {
    stepOneType: MAKE_LESSON_MATERIAL_STEP1_TYPE.단원유형별,
    fixOption: 'NONE', // 복제 후 수정(COPY), 수정 후 재출제(COPY), 수정(EDIT), 출제된 학습지 수정(EDIT_ASSIGNED) 여부
  } as MakeLessonMaterialContext<FixOption, EntryPathType>
  originPublicWorkbookId?: WorkbookEntity.Workbook['id'] // 문제은행 신고를 위해 시중교재를 베이스로 만든 교재나, 학습지로 오답학습지를 만드는 경우 원본 시중교재의 ID가 필요
  weakWorkbookSourceId?: WorkbookEntity.Workbook['id'] // 교재로 오답학습지를 만드는 경우 어떤 교재로 만들었는지 ID 필요
  weakWorkbookPartitionType?: 'PAGE' | 'PROBLEM' // 교재로 오답학습지를 만드는 경우 어떤 타입으로 만들었는지 필요(페이지별 & 문제별)
  constructor(data: Partial<MakeLessonMaterialCommonParams<FixOption, EntryPathType>> = {}) {
    makeAutoObservable(this)

    Object.assign(this, data)
  }

  get params() {
    const query = qs.stringify(
      {
        ...this.entryContext,
        workbookType:
          'workbookType' in this.entryContext ? this.entryContext.workbookType : undefined,
        sameOptionUse: this.sameOptionUse,
        problemSortType: this.problemSortType,
        download: this.download,
        designTemplateId: this.designTemplateId,
        priorityLayoutId: this.priorityLayoutId,
        priorityLayoutColor: this.priorityLayoutColor,
        filterType: this.filterType,
        originPublicWorkbookId: this.originPublicWorkbookId,
        weakWorkbookSourceId: this.weakWorkbookSourceId,
        weakWorkbookPartitionType: this.weakWorkbookPartitionType,
        originalFilterId: this.originalFilterId,
        hasOriginal: this.hasOriginal,
        saveOption: this.saveOption,
        hasSimilar: this.hasSimilar,
        similarX: this.similarX,
        similarLevel: this.similarLevel,
      },
      { skipNulls: true },
    )
    return `${this.filterId}?${query}`
  }
}
